import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import ProfileSection from './components/ProfileSection';
import SubscriptionSection from './components/SubscriptionSection';
import PreferencesSection from './components/PreferencesSection';
import SecuritySection from './components/SecuritySection';
import IreferSection from './components/IreferSection';
import { useAuth } from '../../contexts/AuthContext';

const AccountSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { profile, loading } = useAuth();

  const tabs = [
  { id: 'profile', label: 'Profile', icon: 'User' },
  { id: 'subscription', label: 'Subscription', icon: 'CreditCard' },
  { id: 'preferences', label: 'Preferences', icon: 'Settings' },
  { id: 'security', label: 'Security', icon: 'Shield' },
  { id: 'irefer', label: 'iRefer Integration', icon: 'Link' }];

  const handleProfileUpdate = (updatedData) => {
    console.log('Profile updated:', updatedData);
  };

  const handleSubscriptionUpgrade = (planId) => {
    console.log('Upgrading to plan:', planId);
  };

  const handlePreferencesUpdate = (updatedPreferences) => {
    console.log('Preferences updated:', updatedPreferences);
  };

  const handleSecurityUpdate = (updatedSecurity) => {
    console.log('Security updated:', updatedSecurity);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  // Calculate credit limit and usage
  const getCreditLimit = (tier) => {
    const limits = {
      'free': 50,
      'professional': 500,
      'enterprise': 2000
    };
    return limits?.[tier?.toLowerCase()] || 100;
  };

  const creditLimit = getCreditLimit(profile?.subscription_tier);
  const creditsRemaining = profile?.credits_remaining || 0;
  const creditsUsed = creditLimit - creditsRemaining;

  // Prepare real profile data from database
  const profileData = {
    name: profile?.full_name || '',
    email: profile?.email || '',
    phone: profile?.phone_number || '',
    specialty: profile?.specialization || '',
    licenseNumber: profile?.license_number || '',
    licenseExpiry: "2026-12-31", // Could be added to profile schema
    institution: profile?.institution_name || '',
    avatar: profile?.avatar_url || '',
    avatarAlt: `Professional photo of ${profile?.full_name}`
  };

  // Prepare real subscription data from database
  const subscriptionData = {
    tier: profile?.subscription_tier || 'Free',
    renewalDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    }),
    creditsUsed: creditsUsed,
    creditLimit: creditLimit,
    daysRemaining: 30, // Could calculate based on subscription start date
    monthlyUsage: Math.round((creditsUsed / creditLimit) * 100),
    billingHistory: [] // Could be fetched from a billing table
  };

  // Add missing mock data for preferences and security
  const mockPreferences = {
    language: 'en',
    timezone: 'UTC',
    notifications: {
      email: true,
      sms: false,
      push: true
    },
    theme: 'light'
  };

  const mockSecurityData = {
    twoFactorEnabled: false,
    lastPasswordChange: new Date()?.toLocaleDateString(),
    activeSessions: [
      {
        id: 1,
        device: 'Desktop',
        location: 'New York, USA',
        lastActive: 'Just now',
        current: true
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Account Settings - DiagnosticAI</title>
        <meta name="description" content="Manage your DiagnosticAI account settings, subscription, and clinical preferences" />
      </Helmet>
      <PrimaryNavigation
        userRole={profile?.role || 'clinician'}
        creditBalance={creditsRemaining}
        subscriptionTier={profile?.subscription_tier || 'Free'} />

      <div className="pt-header bg-background">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-foreground mb-2">Account Settings</h1>
            <p className="text-muted-foreground">
              Manage your profile, subscription, and clinical preferences
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-64 flex-shrink-0">
              <div className="clinical-card p-2 sticky top-20">
                <nav className="space-y-1">
                  {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-clinical ${
                    activeTab === tab?.id ?
                    'bg-primary text-primary-foreground' :
                    'text-foreground hover:bg-muted'}`
                    }>

                      <Icon
                      name={tab?.icon}
                      size={18}
                      color={activeTab === tab?.id ? 'white' : 'currentColor'} />

                      <span>{tab?.label}</span>
                    </button>
                  )}
                </nav>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              {activeTab === 'profile' &&
              <ProfileSection
                profileData={profileData}
                onUpdate={handleProfileUpdate} />

              }
              {activeTab === 'subscription' &&
              <SubscriptionSection
                subscriptionData={subscriptionData}
                profile={profile}
                onUpgrade={handleSubscriptionUpgrade} />

              }
              {activeTab === 'preferences' &&
              <PreferencesSection
                preferences={mockPreferences}
                onUpdate={handlePreferencesUpdate} />

              }
              {activeTab === 'security' &&
              <SecuritySection
                securityData={mockSecurityData}
                onUpdate={handleSecurityUpdate} />

              }
              {activeTab === 'irefer' &&
              <IreferSection />
              }
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default AccountSettings;